import type { DscApplicantSection } from "@credit-acceptance/step6-bff-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { type Resolver, useFieldArray, useForm } from "react-hook-form";

import { mapBankSectionToBankAccount } from "./bankMapping";
import { mapIncomeSectionToIncomes } from "./incomeMapping";
import { deriveApplicantSsnType, mapResidenceSectionToCustomerInfo } from "./residenceMapping";
import { type SignersInput, signersSchema } from "./Signers.schema";

// ---------------------------------------------------------------------------
// useSignersForm
//
// Owns all RHF state for the Signers card. Returns the form instance and the
// two field-array instances (driver + secondSigner incomes) so Signers.tsx and
// SignerColumn stay presenter-only.
//
// Presence is derived from `present` in the dashboard response. Income blocks
// are hydrated from each applicant's `income.employments` (see incomeMapping);
// an applicant with no employments seeds exactly ONE blank income block. The
// bank account is hydrated from `bank.bankAccounts` (see bankMapping), and
// customer info from `residence` (see residenceMapping).
// ---------------------------------------------------------------------------

interface UseSignersFormArguments {
  /** Primary applicant (driver) section from the dashboard response. */
  driver?: DscApplicantSection;
  /** Second signer (cosigner) section from the dashboard response. */
  secondSigner?: DscApplicantSection;
}

/**
 * @param {UseSignersFormArguments} args - Driver/2nd-signer sections from the dashboard response.
 * @returns {object} RHF form instance and the driver/2nd-signer income field-array helpers.
 */
export function useSignersForm({ driver, secondSigner }: UseSignersFormArguments = {}) {
  const hasSecondSigner = Boolean(secondSigner?.present);

  // SSN/ITIN status per applicant — gates each column's Income Type list and
  // clamps hydrated income types (see incomeMapping / IncomeBlock).
  const driverSsnType = deriveApplicantSsnType(driver?.residence);
  const secondSignerSsnType = deriveApplicantSsnType(secondSigner?.residence);

  const zodSignersResolver = zodResolver(signersSchema) as unknown as Resolver<SignersInput>;

  const form = useForm<SignersInput>({
    // zodResolver infers the schema's INPUT type: because several fields use
    // .default() and the schema is a ZodEffects (via .superRefine), defaulted
    // fields become `string | undefined` and diverge from SignersInput (the
    // output type the form is typed with). Cast to the form's resolver type —
    // runtime behavior is unchanged (it still validates and returns output).
    //
    // `secondSignerIncomes` below mounts a field array on "secondSigner.incomes"
    // unconditionally (rules of hooks), which makes RHF synthesize
    // `secondSigner: { incomes: [] }` in form state even when there's no 2nd
    // signer — failing `personSchema.optional()` on every submit. Strip it back
    // to undefined before Zod sees it so a driver-only deal can still save.
    resolver: (values, context, options) =>
      zodSignersResolver(hasSecondSigner ? values : { ...values, secondSigner: undefined }, context, options),
    mode: "onBlur",
    defaultValues: {
      driver: {
        incomes: mapIncomeSectionToIncomes(driver?.income, driverSsnType),
        bankAccount: mapBankSectionToBankAccount(driver?.bank),
        customerInfo: mapResidenceSectionToCustomerInfo(driver?.residence),
      },
      // Hydrate the 2nd signer only when the response says one is present;
      // otherwise leave it undefined (matches `personSchema.optional()`) so
      // `incomes.min(1)` validation doesn't run against a hidden column.
      secondSigner: hasSecondSigner
        ? {
            incomes: mapIncomeSectionToIncomes(secondSigner?.income, secondSignerSsnType),
            bankAccount: mapBankSectionToBankAccount(secondSigner?.bank),
            customerInfo: mapResidenceSectionToCustomerInfo(secondSigner?.residence),
          }
        : undefined,
    },
  });

  const { control } = form;

  // Both field arrays are always mounted (React hook rules); the parent decides
  // which columns to render based on the response.
  const driverIncomes = useFieldArray({
    control,
    name: "driver.incomes",
  });

  const secondSignerIncomes = useFieldArray({
    control,
    name: "secondSigner.incomes",
  });

  return {
    form,
    control,
    driverIncomes,
    secondSignerIncomes,
    driverSsnType,
    secondSignerSsnType,
  };
}
